import { useMemo } from 'react';
import { FieldAttribute } from 'common/types/store';
import { useReadFieldAttributes } from 'services/hooks';
import { OPM_CALCULATOR_MODEL_NAME, VALUATIONS_PAGE } from 'services/hooks/useReadFieldAttributes/constants';

/**
 * Precision each numeric OPM input holds, keyed by the calculator's input name. An input is
 * undefined until the field attributes load, and is then left unrounded.
 */
export interface OPMCalculatorDecimalPlaces {
  equityValue?: number;
  riskFreeRate?: number;
  maturity?: number;
  volatility?: number;
}

const readDecimalPlaces = (attributes: FieldAttribute | undefined, fieldName: string) => {
  const decimalPlaces = Number(attributes?.[fieldName]?.decimal_places);
  return Number.isInteger(decimalPlaces) ? decimalPlaces : undefined;
};

/**
 * The `OPMCalculator` column precision from the backend field attributes, which
 * `OPMCalculatorResultsRequestSerializer` mirrors. A value with more decimals is a 400 from the
 * results endpoint, not a rounded number, so inputs are rounded to it before they are sent.
 */
const useOPMCalculatorDecimalPlaces = (): OPMCalculatorDecimalPlaces => {
  const valuationFieldAttributes = useReadFieldAttributes(VALUATIONS_PAGE);

  return useMemo(() => {
    const attributes = valuationFieldAttributes.get(OPM_CALCULATOR_MODEL_NAME) as FieldAttribute | undefined;
    return {
      equityValue: readDecimalPlaces(attributes, 'equity_value'),
      riskFreeRate: readDecimalPlaces(attributes, 'risk_free_rate'),
      maturity: readDecimalPlaces(attributes, 'maturity'),
      volatility: readDecimalPlaces(attributes, 'volatility'),
    };
  }, [valuationFieldAttributes]);
};

export default useOPMCalculatorDecimalPlaces;
